/**
 * AISidebar
 *
 * Collapsible right-side AI assistant panel for the email arche.
 * Chat interface + quick actions + module toggles.
 */

import { useState, useCallback } from 'react';
import { Sparkles, Send, Loader2, Inbox, Trash2, BarChart3, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface AISidebarProps {
  accountId: string | undefined;
  open: boolean;
  onClose: () => void;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

export function AISidebar({ accountId, open, onClose }: AISidebarProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || !accountId) return;

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: input.trim(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    // Simulate AI response — in production this would call the AI chat endpoint
    setTimeout(() => {
      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `I'll help you with that. This feature will be fully connected to the Fias AI backend for natural email interactions.`,
      };
      setMessages((prev) => [...prev, assistantMsg]);
      setLoading(false);
    }, 1000);
  }, [input, accountId]);

  if (!open) return null;

  return (
    <div className="w-80 border-l border-border flex flex-col bg-background h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Fias AI</span>
        </div>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Quick actions */}
      <div className="px-3 py-2 border-b border-border">
        <div className="text-xs font-medium text-muted-foreground mb-1.5">Quick actions</div>
        <div className="flex flex-wrap gap-1">
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
            <Inbox className="h-3 w-3" /> Categorize inbox
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
            <Trash2 className="h-3 w-3" /> Clean up
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
            <BarChart3 className="h-3 w-3" /> Daily digest
          </Button>
        </div>
      </div>

      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-muted-foreground py-8">
            <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Ask me anything about your email</p>
            <div className="mt-3 space-y-1 text-xs">
              <p className="text-muted-foreground">"What emails need a response?"</p>
              <p className="text-muted-foreground">"Summarize my unread messages"</p>
              <p className="text-muted-foreground">"Draft a follow-up to John"</p>
            </div>
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={cn(
              'rounded-lg px-3 py-2 text-sm max-w-[90%]',
              msg.role === 'user' ? 'bg-primary text-primary-foreground ml-auto' : 'bg-muted',
            )}
          >
            {msg.content}
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Thinking...
          </div>
        )}
      </div>

      {/* Input */}
      <div className="px-3 py-2 border-t border-border">
        <div className="flex gap-1">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about your email..."
            className="h-8 text-sm"
            onKeyDown={(e) => {
              if (e.key === 'Enter') sendMessage();
            }}
          />
          <Button
            size="sm"
            className="h-8 w-8 p-0"
            onClick={sendMessage}
            disabled={loading || !input.trim()}
          >
            <Send className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
